import React from 'react';
import { CheckCircle2, Circle } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password }) => {
  const hasLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  const criteria = [
    { label: 'At least 8 characters', met: hasLength },
    { label: 'At least one uppercase letter (A-Z)', met: hasUpper },
    { label: 'At least one lowercase letter (a-z)', met: hasLower },
    { label: 'At least one number (0-9)', met: hasNumber },
    { label: 'At least one special character (!@#$...)', met: hasSpecial },
  ];

  const metCount = criteria.filter((c) => c.met).length;
  
  let strengthLabel = 'Weak';
  let strengthColor = 'bg-rose-500';
  let textColor = 'text-rose-600';
  if (metCount >= 4) {
    strengthLabel = 'Good';
    strengthColor = 'bg-amber-500';
    textColor = 'text-amber-600';
  }
  if (metCount === 5) {
    strengthLabel = 'Strong';
    strengthColor = 'bg-emerald-500';
    textColor = 'text-emerald-600';
  }

  if (!password) return null;

  return (
    <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5 text-xs">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-slate-700">Password Security:</span>
        <span className={`font-bold ${textColor}`}>
          {strengthLabel} ({metCount}/5)
        </span>
      </div>

      {/* Strength Bar */}
      <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden flex gap-0.5">
        <div
          className={`h-full transition-all duration-300 ${strengthColor}`}
          style={{ width: `${(metCount / 5) * 100}%` }}
        />
      </div>

      {/* Checklist */}
      <div className="grid grid-cols-1 gap-1.5 pt-1">
        {criteria.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            {item.met ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            )}
            <span className={item.met ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
